/**
 * OUROBOROS — entry point.
 *
 * Owns the clock and nothing else. The simulation is stepped on a fixed tick
 * derived from the current coil; rendering runs at display rate and
 * interpolates between the last two ticks. Everything the sim emits comes back
 * as a list of events, which is the single place sound, particles and toasts
 * are triggered from — so the feel of the game lives in one readable function.
 */

import { botTurn } from './core/bot'
import { encodeTurns, verifyReplay } from './core/replay'
import type { Turn } from './core/replay'
import { coilName, rulesFor, tickMs } from './core/rules'
import { createSim, step, summarize } from './core/sim'
import { dailySeed, utcDayKey } from './core/rng'
import type { Dir, ModeId, Rules, SimState, Vec } from './core/types'
import { queueTurn } from './core/sim'
import { Audio } from './audio/audio'
import { Renderer } from './render/renderer'
import { applyDaltonism, heatColor } from './render/palette'
import { store, type Settings } from './storage'
import { Input } from './ui/input'
import { flushPending, hasOnlineBoard, submitRun } from './ui/leaderboard'
import { roman, UI, type ScreenName } from './ui/ui'

type Phase = 'attract' | 'playing' | 'paused' | 'over'

const C_CYAN: [number, number, number] = [94, 240, 255]
const C_GOLD: [number, number, number] = [255, 194, 77]
const C_MAGENTA: [number, number, number] = [224, 92, 255]
const C_RED: [number, number, number] = [255, 59, 92]

class Game {
  private canvas = document.getElementById('board') as HTMLCanvasElement
  private renderer = new Renderer(this.canvas)
  private audio = new Audio()
  private input = new Input()
  private ui: UI

  private mode: ModeId
  private rules: Rules
  private sim: SimState
  private prevSnake: Vec[] = []
  private turns: Turn[] = []

  private phase: Phase = 'attract'
  private acc = 0
  private lastFrame = 0
  private clock = 0
  private grace = 0
  private heartbeatAt = 0
  /** Unpaused seconds in the current run — pause time shouldn't count. */
  private runElapsed = 0
  private submitted = false

  constructor() {
    this.mode = store.settings.lastMode ?? 'ouroboros'
    this.rules = rulesFor(this.mode)
    this.sim = createSim(this.rules, this.freshSeed())

    this.ui = new UI(
      {
        onPlay: () => this.play(),
        onResume: () => this.resume(),
        onRestart: () => this.play(),
        onQuit: () => this.toAttract(),
        onModeChange: (m) => this.setMode(m),
        onSetting: (patch) => this.applySettings(patch),
        onSubmit: (name) => void this.submit(name),
        onNavigate: (s) => this.navigate(s),
      },
      this.mode,
    )

    this.wireInput()
    this.applySettings({})
    this.ui.show('title')
    void flushPending()

    requestAnimationFrame((t) => {
      this.lastFrame = t
      requestAnimationFrame(this.frame)
    })
  }

  /**
   * Manual stepping for development and for automated screenshots — a
   * headless or backgrounded tab throttles requestAnimationFrame, so there has
   * to be a way to advance the game that doesn't depend on the display.
   */
  devStep(ticks: number, dir?: Dir, autopilot = false): void {
    if (dir !== undefined) this.turn(dir)
    for (let i = 0; i < ticks; i++) {
      if (this.phase !== 'playing' && this.phase !== 'attract') break
      if (autopilot) {
        const d = botTurn(this.sim, this.rules.biteEnabled)
        if (d !== null) this.turn(d)
      }
      this.doTick()
      // Advance the presentation clock by a tick's worth too, so effects age
      // exactly as they would in a live run and screenshots are honest.
      const secs = tickMs(this.rules, this.sim.coil, this.sim.recoil) / 1000
      this.clock += secs
      if (this.phase === 'playing') this.runElapsed += secs
      this.draw(secs)
    }
    this.grace = 0
    this.acc = 0
    this.lastFrame = performance.now()
  }

  /** Snapshot for the console API and for debugging. Structurally cloned. */
  debugState(): Record<string, unknown> {
    const s = this.sim
    return JSON.parse(
      JSON.stringify({
        phase: this.phase,
        mode: this.mode,
        tick: s.tick,
        score: s.score,
        light: Math.round(s.light * 10) / 10,
        chain: s.chain,
        coil: s.coil,
        length: s.snake.length,
        biteCooldown: s.biteCooldown,
        phaseTicks: s.phase,
        alive: s.alive,
        cause: s.cause,
        pickups: s.pickups,
        blooms: s.blooms,
        head: s.snake[0],
        turnsRecorded: this.turns.length,
      }),
    )
  }

  // ── lifecycle ──────────────────────────────────────────────────────────

  private freshSeed(): number {
    if (this.mode === 'daily') return dailySeed(utcDayKey())
    const buf = new Uint32Array(1)
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(buf)
    else buf[0] = (Math.random() * 0xffffffff) >>> 0
    return buf[0] >>> 0
  }

  private setMode(mode: ModeId): void {
    if (mode === this.mode) return
    this.mode = mode
    this.rules = rulesFor(mode)
    store.saveSettings({ lastMode: mode })
    this.ui.setMode(mode)
    this.audio.ui('move')
    if (this.phase === 'attract') this.newSim()
  }

  private newSim(): void {
    this.sim = createSim(this.rules, this.freshSeed())
    this.prevSnake = this.sim.snake.map((s) => ({ ...s }))
    this.turns = []
    this.acc = 0
    this.renderer.reset()
  }

  private play(): void {
    this.audio.unlock()
    // First-timers get the rules once, unprompted, then never again.
    if (!store.settings.seenTutorial) {
      store.saveSettings({ seenTutorial: true })
      this.navigate('help')
      return
    }
    this.newSim()
    this.submitted = false
    this.phase = 'playing'
    this.runElapsed = 0
    this.grace = 900
    this.ui.show('none')
    this.audio.ui('select')
    this.ui.toast(`Coil ${roman(1)} · ${coilName(1)}`)
    if (this.mode === 'daily') this.ui.toast(utcDayKey(), 'magenta')
  }

  private pause(): void {
    if (this.phase !== 'playing') return
    this.phase = 'paused'
    this.ui.show('pause')
    this.audio.ui('back')
  }

  private resume(): void {
    if (this.phase !== 'paused') return
    this.phase = 'playing'
    this.grace = Math.max(this.grace, 550)
    this.ui.show('none')
    this.audio.ui('select')
  }

  private toAttract(): void {
    this.phase = 'attract'
    this.newSim()
    this.ui.show('title')
    this.audio.ui('back')
  }

  private navigate(screen: ScreenName): void {
    if (screen === 'none') {
      this.ui.show(this.phase === 'attract' ? 'title' : 'none')
      return
    }
    // Opening a menu mid-run should never cost you the run.
    if (this.phase === 'playing' && (screen === 'settings' || screen === 'help' || screen === 'board')) {
      this.phase = 'paused'
    }
    this.ui.show(screen)
    this.audio.ui(screen === 'title' ? 'back' : 'move')
  }

  private gameOver(): void {
    this.phase = 'over'
    this.renderer.markDeath(this.clock)
    const run = summarize(this.sim)
    const isRecord = store.recordRun(run, store.settings.name || 'anon')
    const canSubmit = hasOnlineBoard() && run.score > 0
    this.ui.showGameOver(run, isRecord, canSubmit, this.runElapsed)
  }

  private async submit(name: string): Promise<void> {
    if (this.submitted) return
    const clean = name.replace(/[^\w \-.'!?]/g, '').slice(0, 16) || 'anon'
    store.saveSettings({ name: clean })

    // Verify our own replay before asking the server to. If this fails, the
    // bug is ours, and silently shipping a rejection would be worse.
    const run = summarize(this.sim)
    const local = verifyReplay({
      v: 1,
      mode: run.mode,
      seed: run.seed,
      turns: encodeTurns(this.turns),
    })
    if (!local || local.score !== run.score) {
      this.ui.setSubmitStatus('This run could not be verified locally, so it was not submitted.', 'bad')
      return
    }

    this.ui.setSubmitting(true)
    this.ui.setSubmitStatus('Submitting…')
    const res = await submitRun({ run, turns: this.turns, name: clean })
    this.ui.setSubmitting(false)

    if (res.ok) {
      this.submitted = true
      this.ui.hideSubmit()
      this.ui.setSubmitStatus(
        res.rank ? `Ranked #${res.rank}${res.total ? ` of ${res.total}` : ''}.` : 'Submitted.',
        'good',
      )
    } else {
      this.ui.setSubmitStatus(res.message ?? 'Could not submit.', 'bad')
    }
  }

  // ── input ──────────────────────────────────────────────────────────────

  private wireInput(): void {
    this.input.attach(document.getElementById('stage') as HTMLElement)
    this.input.onAny = () => this.audio.unlock()
    this.input.onTurn = (dir) => this.turn(dir)
    this.input.onAction = (action) => {
      switch (action) {
        case 'pause':
          if (this.phase === 'playing') this.pause()
          else if (this.phase === 'paused') this.resume()
          else if (this.phase === 'attract') this.play()
          break
        case 'restart':
          if (this.phase !== 'attract') this.play()
          break
        case 'back':
          if (this.ui.screen === 'help' || this.ui.screen === 'settings' || this.ui.screen === 'board') this.ui.back()
          else if (this.phase === 'playing') this.pause()
          else if (this.phase === 'paused') this.resume()
          break
        case 'confirm':
          if (this.phase === 'attract') this.play()
          else if (this.phase === 'over') this.play()
          break
        case 'mute':
          this.applySettings({ muted: !store.settings.muted })
          break
      }
    }

    for (const el of document.querySelectorAll<HTMLElement>('.pad-btn')) {
      this.input.bindButton(el, Number(el.dataset.dir) as Dir)
    }
    document.getElementById('btn-pause')?.addEventListener('click', () => {
      if (this.phase === 'playing') this.pause()
      else if (this.phase === 'paused') this.resume()
      else if (this.phase === 'attract') this.play()
    })
    document.getElementById('btn-mute')?.addEventListener('click', () => {
      this.audio.unlock()
      this.applySettings({ muted: !store.settings.muted })
    })

    // Tabbing away mid-run is not a death sentence.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.phase === 'playing') this.pause()
    })
    window.addEventListener('blur', () => {
      if (this.phase === 'playing') this.pause()
    })
  }

  private turn(dir: Dir): void {
    if (this.phase === 'attract') {
      this.play()
      return
    }
    if (this.phase !== 'playing') return
    // Only accepted turns go in the log — the replay must see exactly what the
    // simulation saw, or verification diverges.
    if (queueTurn(this.sim, dir)) this.turns.push({ tick: this.sim.tick, dir })
  }

  private applySettings(patch: Partial<Settings>): void {
    const s = store.saveSettings(patch)
    this.audio.setMuted(s.muted)
    this.audio.setMusic(s.music && !s.muted)
    applyDaltonism(s.daltonize)
    this.ui.syncSettings()
  }

  // ── loop ───────────────────────────────────────────────────────────────

  private frame = (now: number): void => {
    const dt = Math.min(120, now - this.lastFrame)
    this.lastFrame = now
    this.clock += dt / 1000
    this.input.pollGamepad()

    if (this.phase === 'playing') this.runElapsed += dt / 1000

    const running = this.phase === 'playing' || this.phase === 'attract'
    if (running) {
      if (this.grace > 0) this.grace -= dt
      else {
        this.acc += dt
        let guard = 0
        for (;;) {
          const interval = tickMs(this.rules, this.sim.coil, this.sim.recoil)
          if (this.acc < interval || guard++ > 6) break
          this.acc -= interval
          this.doTick()
          if (this.phase !== 'playing' && this.phase !== 'attract') break
        }
      }
    }

    this.draw(dt / 1000)
    requestAnimationFrame(this.frame)
  }

  private draw(dt: number): void {
    const interval = tickMs(this.rules, this.sim.coil, this.sim.recoil)
    const alpha = this.phase === 'over' || this.grace > 0 ? 1 : Math.min(1, this.acc / interval)
    const settings = store.settings

    this.renderer.render({
      sim: this.sim,
      prev: this.prevSnake,
      alpha,
      rules: this.rules,
      dt,
      time: this.clock,
      paused: this.phase === 'paused',
      reducedMotion: settings.reducedMotion,
      showLattice: settings.showLattice,
      showScanlines: settings.showScanlines,
    })

    if (this.phase === 'attract') this.ui.syncHudIdle(this.rules)
    else this.ui.syncHud(this.sim, this.rules)
  }

  private doTick(): void {
    const s = this.sim

    if (this.phase === 'attract') {
      const dir = botTurn(s, this.rules.biteEnabled)
      if (dir !== null) queueTurn(s, dir)
    }

    this.prevSnake = s.snake.map((p) => ({ ...p }))
    step(s, this.rules)
    this.audio.pulse(s.coil, s.alive)

    if (this.rules.lightEnabled) {
      const ratio = s.light / this.rules.lightMax
      this.audio.setIntensity(Math.min(1, (s.coil - 1) / 6 + (1 - ratio) * 0.4))
      if (ratio < 0.22 && s.alive && this.clock - this.heartbeatAt > 1.1) {
        this.heartbeatAt = this.clock
        if (this.phase === 'playing') this.audio.heartbeat()
      }
    }

    this.handleEvents()

    if (!s.alive) {
      if (this.phase === 'attract') this.newSim()
      else this.gameOver()
    }
  }

  /** The one place feel is authored: sim events in, sound and light out. */
  private handleEvents(): void {
    const s = this.sim
    const quiet = this.phase === 'attract'
    const p = this.renderer.particles
    const heat = this.rules.chainMax > 0 ? s.chain / this.rules.chainMax : 0

    for (const ev of s.events) {
      switch (ev.type) {
        case 'eat': {
          const at = { x: ev.x + 0.5, y: ev.y + 0.5 }
          if (ev.kind === 'mote') {
            p.burst(at.x, at.y, 22, heatColor(heat), 7, 0.55)
            p.ring(at.x, at.y, C_CYAN)
            if (!quiet) {
              this.audio.eat(ev.chain)
              this.renderer.shake(2 + ev.chain * 0.35)
              if (ev.chain >= 3) this.ui.toast(`+${ev.points}  ×${(1 + ev.chain * 0.25).toFixed(2)}`, 'gold')
            }
          } else if (ev.kind === 'ember') {
            p.burst(at.x, at.y, 10, C_GOLD, 5, 0.4)
            if (!quiet) this.audio.ember()
          } else {
            p.burst(at.x, at.y, 40, C_MAGENTA, 10, 0.9)
            this.renderer.shock(ev.x, ev.y, C_MAGENTA, 1)
            if (!quiet) {
              this.audio.prism()
              this.renderer.flash(C_MAGENTA, 0.5)
              this.renderer.shake(8)
              this.ui.toast('Phase', 'magenta')
            }
          }
          break
        }

        case 'bite': {
          // The signature moment — it should hit like something broke.
          p.burst(ev.x + 0.5, ev.y + 0.5, 60, C_GOLD, 13, 1.1)
          this.renderer.shock(ev.x, ev.y, C_GOLD, 1.1)
          if (!quiet) {
            this.audio.bite(ev.severed)
            this.renderer.shake(16)
            this.renderer.flash(C_GOLD, 0.65)
            this.ui.toast(`Severed ×${ev.severed}  +${ev.points}`, 'gold')
          }
          break
        }

        case 'coil':
          this.renderer.shock(s.snake[0].x, s.snake[0].y, C_CYAN, 1.4)
          if (!quiet) {
            this.audio.coil(ev.coil)
            this.renderer.flash(C_CYAN, 0.35)
            this.ui.toast(`Coil ${roman(ev.coil)} · ${coilName(ev.coil)}`)
          }
          break

        case 'fangs':
          if (!quiet) {
            this.audio.fangs()
            this.ui.toast('Fangs regrown', 'gold')
          }
          break

        case 'lowLight':
          if (!quiet) this.ui.toast('Light failing', 'danger')
          break

        case 'chainBreak':
          if (!quiet && ev.at >= 4) this.ui.toast('Chain lost', 'danger')
          break

        case 'phase':
          if (!ev.on && !quiet) this.ui.toast('Phase ended', 'magenta')
          break

        case 'death': {
          p.burst(ev.x + 0.5, ev.y + 0.5, 120, C_RED, 16, 1.5)
          for (const seg of s.snake) p.burst(seg.x + 0.5, seg.y + 0.5, 3, C_RED, 5, 0.9)
          this.renderer.shock(ev.x, ev.y, C_RED, 1.6)
          if (!quiet) {
            this.audio.death()
            this.renderer.shake(24)
            this.renderer.flash(C_RED, 0.8)
          }
          break
        }
      }
    }
  }
}

const game = new Game()

// The 2022 version advertised a console API in its README; keeping one is both
// a nod to that and genuinely how this thing gets debugged. Read-only: the
// leaderboard verifies by replay, so poking at state here can't buy you a rank.
Object.defineProperty(window, 'ouroboros', {
  value: Object.freeze({
    get state() {
      return game.debugState()
    },
    version: '2.0.0',
    help: 'ouroboros.state — a snapshot of the current run.',
  }),
  writable: false,
})

if (__DEV__) {
  Object.assign(window as unknown as Record<string, unknown>, {
    __dev: {
      step: (ticks = 1, dir?: Dir) => game.devStep(ticks, dir),
      auto: (ticks = 100) => game.devStep(ticks, undefined, true),
      state: () => game.debugState(),
    },
  })
}
